import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// Importamos la configuración que ya usas en otros componentes para el soporte local
import { API_URLS, BASE_URL } from '../../apiConfig'; 

function ReportesView() {
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [filtros, setFiltros] = useState({
        fechaInicial: '',
        fechaFinal: '',
        tipo: '', 
        codigo: ''
    });

    const token = localStorage.getItem('token');
    
    const fetchMovimientos = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        const params = new URLSearchParams();
        if (filtros.fechaInicial) params.append('fechaInicial', filtros.fechaInicial);
        if (filtros.fechaFinal) params.append('fechaFinal', filtros.fechaFinal);
        if (filtros.tipo) params.append('tipo', filtros.tipo);
        if (filtros.codigo) params.append('codigo', filtros.codigo);

        try {
            // Usamos BASE_URL para que cambie automáticamente entre local y nube
            const url = `${BASE_URL}/movimientos/reporte?${params.toString()}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMovimientos(response.data);
        } catch (err) {
            setError('Error de conexión con el servidor de reportes.');
            setMovimientos([]);
        } finally {
            setLoading(false);
        }
    }, [token, filtros]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchMovimientos();
    };

    const limpiarFiltros = () => {
        setFiltros({
            fechaInicial: '',
            fechaFinal: '',
            tipo: '', 
            codigo: ''
        });
    };

    useEffect(() => {
        fetchMovimientos();
    }, [fetchMovimientos]);

    const exportToCSV = () => {
        if (movimientos.length === 0) return;
        // Agregamos "Accesorios" a los encabezados del Excel/CSV
        const headers = ["ID", "Fecha", "Codigo", "Tipo", "Destino", "Receptor", "Accesorios", "Observacion"];
        const rows = movimientos.map(mov => [
            mov.id,
            new Date(mov.fecha).toLocaleString(),
            mov.codigo_producto,
            mov.tipo_movimiento,
            mov.destino || 'N/A',
            mov.receptor || 'N/A',
            `"${mov.accesorios || 'Ninguno'}"`, // Escapamos con comillas por si hay comas en el texto
            `"${mov.observacion || ''}"`
        ]);
        const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Inventario_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="form-container-responsive animate-fadeIn">
            {/* ENCABEZADO */}
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] block mb-1">Módulo de Auditoría</span>
                    <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <i className="bi bi-shield-check text-blue-600"></i>
                        📊 Reporte de Activos
                    </h2>
                </div>
                <div className="flex gap-2">
                    <button onClick={limpiarFiltros} className="btn-light-modern px-4 py-2 border rounded-xl text-[11px] font-bold hover:bg-gray-100 transition-all">
                        <i className="bi bi-eraser-fill me-2"></i> Limpiar
                    </button>
                    <button 
                        onClick={exportToCSV}
                        disabled={movimientos.length === 0}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-[11px] font-bold hover:bg-emerald-700 transition-all shadow-sm disabled:opacity-50"
                    >
                        <i className="bi bi-download me-2"></i> Exportar CSV
                    </button>
                </div>
            </div>

            {/* PANEL DE FILTROS */}
            <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 mb-8">
                <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[180px]">
                        <label className="label-modern !text-[10px]">Código Activo</label>
                        <input type="text" name="codigo" value={filtros.codigo} onChange={handleFilterChange} placeholder="Buscar código..." className="input-modern !py-2" />
                    </div>
                    <div className="w-40">
                        <label className="label-modern !text-[10px]">Desde</label>
                        <input type="date" name="fechaInicial" value={filtros.fechaInicial} onChange={handleFilterChange} className="input-modern !py-2" />
                    </div>
                    <div className="w-40">
                        <label className="label-modern !text-[10px]">Hasta</label>
                        <input type="date" name="fechaFinal" value={filtros.fechaFinal} onChange={handleFilterChange} className="input-modern !py-2" />
                    </div>
                    <div className="w-40">
                        <label className="label-modern !text-[10px]">Tipo</label>
                        <select name="tipo" value={filtros.tipo} onChange={handleFilterChange} className="input-modern !py-2">
                            <option value="">Todos</option>
                            <option value="entrada">Entradas</option>
                            <option value="salida">Salidas</option>
                        </select>
                    </div>
                    <button type="submit" className="bg-gray-800 hover:bg-blue-600 text-white px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all h-[42px] flex items-center shadow-md">
                        {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : <><i className="bi bi-search me-2"></i> Filtrar</>}
                    </button>
                </form>
            </div>

            {/* TABLA DE RESULTADOS */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                {error ? (
                    <div className="p-10 text-center text-red-500 font-bold">{error}</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50/50 border-b border-gray-100">
                                    <th className="px-6 py-4">Fecha / Hora</th>
                                    <th className="px-6 py-4">Operación</th>
                                    <th className="px-6 py-4">Activo</th>
                                    <th className="px-6 py-4">Destino / Receptor</th>
                                    <th className="px-6 py-4">Accesorios</th>
                                    <th className="px-6 py-4">Notas</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {movimientos.length > 0 ? (
                                    movimientos.map((mov) => (
                                        <tr key={mov.id} className="hover:bg-blue-50/20 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="text-[11px] font-bold text-gray-600">{new Date(mov.fecha).toLocaleDateString()}</div>
                                                <div className="text-[9px] text-gray-400">{new Date(mov.fecha).toLocaleTimeString()}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase ${
                                                    mov.tipo_movimiento === 'entrada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {mov.tipo_movimiento}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-bold text-gray-800">
                                                {mov.codigo_producto}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-xs font-bold text-gray-700">{mov.destino || '---'}</div>
                                                <div className="text-[10px] text-gray-400">{mov.receptor || 'No especificado'}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {mov.accesorios ? mov.accesorios.split(',').map((acc, i) => (
                                                        <span key={i} className="bg-gray-100 text-gray-500 text-[9px] px-1.5 py-0.5 rounded border border-gray-200">
                                                            {acc.trim()}
                                                        </span>
                                                    )) : <span className="text-[10px] text-gray-300 italic">Ninguno</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-[11px] text-gray-400 italic max-w-[200px] truncate">
                                                {mov.observacion || 'Sin notas'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-16 text-center text-gray-300 text-xs italic">
                                            No se encontraron movimientos registrados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReportesView;